import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { mdiRobotOutline } from '@mdi/js'
import type { AgentMission, AgentRun } from '../../api/schema'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { AGENT_RUN_STATUS, displayStatus, toneVariant } from './agent-run-status'
import { AgentRunOutputs } from './AgentRunOutputs'

/** The error codes agent-runs:errors.<code> knows; anything else shows raw. */
export const KNOWN_RUN_ERRORS = [
    'subscription_limit',
    'max_turns',
    'deadline',
    'bridge_shutdown',
    'bundle_invalid',
    'bundle_too_large',
    'harness_start_failed',
    'prompt_render_failed',
    'AGENT_PROVIDER_NOT_ALLOWED',
    'AGENT_DIRECT_NEEDS_INTERNAL',
] as const

export const PENDING_TYPES = [
    'asset',
    'contact',
    'contract',
    'asset_data',
    'contract_data',
    'plan_proposal',
    'intervention',
    'contract_value',
    'contract_asset',
    'document_link',
    'asset_relation',
] as const

type Decision = 'approve' | 'reject' | 'cancel'

/** Past this length a summary may run over its 4 folded lines. */
const SUMMARY_FOLD_CHARS = 320

/** runErrorText reads a run's error code: its message, the exit code, or the raw code. */
export function runErrorText(t: (key: string, opts?: Record<string, unknown>) => string, error: string): string {
    const exit = /^exit_code:(-?\d+)$/.exec(error)
    if (exit) return t('errors.exit_code', { code: exit[1] })
    if ((KNOWN_RUN_ERRORS as readonly string[]).includes(error)) return t(`errors.${error}`)
    return error
}

/** The harness and model of a run, "pi · Qwen3.8-27B". */
export function pairLabel(t: (key: string, opts?: Record<string, unknown>) => string, harness: string, model: string): string {
    return `${t(`harnesses.${harness}`, { defaultValue: harness })} · ${model}`
}

export interface AgentRunCardProps {
    org: string
    run: AgentRun
    mission?: AgentMission
    /** data.write: approve, reject, cancel. */
    canDecide: boolean
    /** data.write or a platform admin: the Details link. */
    canOpenDetails: boolean
    /** The target's frame for the editor link, when the run targets a floor. */
    floorAssetUid?: string
    floorNames?: Record<string, string>
    onDecision?: (decision: Decision) => Promise<string | undefined>
    /** Hide the header (the run page has its own). */
    bodyOnly?: boolean
}

/**
 * An agent run where its work lands (pattern 49): the Agent badge, the step
 * the agent declared, the pending proposals and the mission's outputs, with
 * grouped decisions confirmed and kept to data.write. Presentational: the
 * panel and the patterns page feed it.
 */
export function AgentRunCard({
    org,
    run,
    mission,
    canDecide,
    canOpenDetails,
    floorAssetUid,
    floorNames,
    onDecision,
    bodyOnly = false,
}: AgentRunCardProps) {
    const { t } = useTranslation('agent-runs')
    const [confirm, setConfirm] = useState<Decision | null>(null)
    const [busy, setBusy] = useState(false)
    const [refusal, setRefusal] = useState<string | undefined>()
    const [summaryOpen, setSummaryOpen] = useState(false)

    const status = displayStatus(run)
    const statusConfig = AGENT_RUN_STATUS[status]
    const active = run.status === 'queued' || run.status === 'running'
    const pending = run.pending_proposals
    const missionTitle = mission ? t(mission.title_key, { defaultValue: mission.code }) : run.mission_code
    const step = run.current_step

    const decide = async (decision: Decision) => {
        if (!onDecision) return
        setBusy(true)
        setRefusal(undefined)
        try {
            setRefusal(await onDecision(decision))
        } finally {
            setBusy(false)
            setConfirm(null)
        }
    }

    const confirmText = () => {
        if (confirm === 'approve') return t('card.confirm_approve', { count: pending.total })
        if (confirm === 'reject') return t('card.confirm_reject', { count: pending.total })
        return t('card.confirm_cancel')
    }

    const body = (
        <div className="space-y-4">
            <div className="text-sm text-foreground" data-testid="agent-run-step">
                {run.status === 'queued' ? (
                    <span className="text-muted-foreground">{t('card.waiting')}</span>
                ) : step ? (
                    <>
                        <span className="font-medium">
                            {step.position > 0 ? `${step.position}/${step.steps_total} · ` : ''}
                            {step.title_key ? t(step.title_key, { defaultValue: step.code }) : step.code}
                        </span>
                        {step.done != null && step.total != null && (
                            <span className="ml-2 tabular-nums text-muted-foreground">{step.done}/{step.total}</span>
                        )}
                        {step.note && <span className="ml-2 text-muted-foreground">{step.note}</span>}
                    </>
                ) : (
                    <span className="text-muted-foreground">{active ? t('card.starting') : t('card.no_step')}</span>
                )}
            </div>

            {pending.total > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="agent-run-pending">
                    <span className="text-sm font-medium text-foreground">{t('card.pending', { count: pending.total })}</span>
                    {PENDING_TYPES.filter((type) => pending[type] > 0).map((type) => (
                        <Badge key={type} variant="warning" data-testid={`agent-run-pending-${type}`}>
                            {t(`card.pending_types.${type}`, { count: pending[type] })}
                        </Badge>
                    ))}
                    {floorAssetUid && pending.plan_proposal > 0 && (
                        <Link
                            to="/$org/assets/$assetUid/plan-editor"
                            params={{ org, assetUid: floorAssetUid }}
                            className="text-sm text-primary hover:underline"
                            data-testid="agent-run-open-editor"
                        >
                            {t('card.open_editor')}
                        </Link>
                    )}
                </div>
            )}

            <AgentRunOutputs org={org} output={run.output} mission={mission} floorNames={floorNames} />

            {run.error && (
                <div className="rounded-md border border-destructive/25 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="agent-run-error">
                    {runErrorText(t, run.error)}
                </div>
            )}

            {run.summary && (
                <div data-testid="agent-run-summary">
                    {/* The agent writes its summary in markdown (#1703). */}
                    <div className={`prose prose-sm dark:prose-invert max-w-none text-sm text-muted-foreground ${summaryOpen ? '' : 'line-clamp-4'}`}>
                        <Markdown remarkPlugins={[remarkGfm]}>{run.summary}</Markdown>
                    </div>
                    {(run.summary.length > SUMMARY_FOLD_CHARS || run.summary.split('\n').length > 4) && (
                        <Button
                            variant="link"
                            size="sm"
                            className="h-auto px-0"
                            onClick={() => setSummaryOpen((o) => !o)}
                            data-testid="agent-run-summary-toggle"
                        >
                            {summaryOpen ? t('card.summary_less') : t('card.summary_more')}
                        </Button>
                    )}
                </div>
            )}

            {run.decision && (
                <div className="text-sm text-muted-foreground" data-testid="agent-run-decision">
                    {t(`card.decision_${run.decision}`, { name: run.decided_by?.name ?? '' })}
                </div>
            )}

            {refusal && (
                <div className="text-sm text-destructive" data-testid="agent-run-decision-error">{refusal}</div>
            )}

            {(canDecide || canOpenDetails) && (
                <div className="flex flex-wrap gap-2">
                    {canDecide && !active && pending.total > 0 && (
                        <>
                            <Button size="sm" onClick={() => setConfirm('approve')} disabled={busy} data-testid="agent-run-approve-all">
                                {t('card.approve_all')}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setConfirm('reject')} disabled={busy} data-testid="agent-run-reject-all">
                                {t('card.reject_all')}
                            </Button>
                        </>
                    )}
                    {canDecide && active && (
                        <Button size="sm" variant="outline" onClick={() => setConfirm('cancel')} disabled={busy || status === 'cancelling'} data-testid="agent-run-cancel">
                            {t('card.cancel')}
                        </Button>
                    )}
                    {canOpenDetails && !bodyOnly && (
                        <Button size="sm" variant="ghost" asChild data-testid="agent-run-details">
                            <Link to="/$org/agent-runs/$runUid" params={{ org, runUid: run.uid }}>
                                {t('card.details')}
                            </Link>
                        </Button>
                    )}
                </div>
            )}

            <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
                <AlertDialogContent data-testid="agent-run-decision-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirm === 'approve' ? t('card.approve_all') : confirm === 'reject' ? t('card.reject_all') : t('card.cancel')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>{confirmText()}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="agent-run-decision-dismiss">{t('card.keep')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault()
                                if (confirm) void decide(confirm)
                            }}
                            disabled={busy}
                            data-testid="agent-run-decision-submit"
                        >
                            {t('card.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )

    if (bodyOnly) return body

    return (
        <section className="rounded-lg border border-border bg-card p-4 space-y-4" data-testid={`agent-run-card-${run.uid}`}>
            <div className="flex flex-wrap items-center gap-2">
                <Badge variant="info" className="gap-1" data-testid="agent-run-badge">
                    <AppIcon path={mdiRobotOutline} size="xs" />
                    {t('card.badge')}
                </Badge>
                <span className="font-semibold text-foreground">{missionTitle}</span>
                <span className="text-sm text-muted-foreground">{pairLabel(t, run.harness, run.model)}</span>
                <Badge variant={toneVariant(statusConfig.tone)} className="ml-auto gap-1" data-testid="agent-run-status">
                    <AppIcon path={statusConfig.icon} size="xs" />
                    {t(statusConfig.labelKey)}
                </Badge>
            </div>
            {body}
        </section>
    )
}
